"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal } from "@/components/shared/motion";
import { Smartphone, ArrowRight } from "lucide-react";

/** Market-test (market-test-plan.md §3): an in-flow compatibility nudge reduces
 *  pre-purchase hesitation (research/07 CF5). */
const COMPAT_EXPERIMENT_ID = "wf_inflow_compatibility_v1";

/**
 * AquaGlobe compatibility teaser — a frosted-glass banner nudging travelers to
 * the device checker. Glowing glass icon + teal pill CTA to /compatible-phones/.
 * Glass-native.
 */
export default function CompatTeaser() {
  const { t } = useTranslations();

  const onCheck = () => {
    try {
      track({
        event_name: "compatibility_checker_started",
        event_id: `compatibility_checker_started:${COMPAT_EXPERIMENT_ID}:teaser:${Date.now()}`,
        experiment_id: COMPAT_EXPERIMENT_ID,
        variant_id: "homepage_teaser",
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <section className="container mx-auto px-4 py-8">
      <Reveal as="div" className="aqua-glass rounded-3xl p-6 backdrop-blur flex flex-col sm:flex-row items-center justify-between gap-5" y={18}>
        <div className="flex items-center gap-4">
          <span className="aqua-icon inline-flex items-center justify-center rounded-2xl p-3 backdrop-blur shrink-0">
            <Smartphone className="h-6 w-6 text-primary" />
          </span>
          <div>
            <div className="font-semibold text-foreground">
              {t("aqua.compat.title", "Is your device eSIM compatible?")}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("aqua.compat.body", "Check your phone in seconds before you travel.")}
            </div>
          </div>
        </div>
        <Link
          href="/compatible-phones/"
          onClick={onCheck}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 h-11 shrink-0 shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all active:scale-[0.98]"
        >
          {t("aqua.compat.cta", "Check compatibility")} <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </section>
  );
}
